<template>
    <div class="vehicle-insurance">
        <BaloiseInsuranceOptions v-if="isBaloiseInsurance" :items="allInsuranceAddons" :vehicle="vehicle" />
        <OmocomInsuranceOptions
            v-else-if="isOmocomInsurance"
            :items="allInsuranceAddons"
            :vehicle="vehicle"
            :is-interior-insurance-included="isInteriorInsuranceIncluded"
        />
        <OwnersInsuranceOptions v-else :items="allInsuranceAddons" :vehicle="vehicle" />
    </div>
</template>

<script>
import {
    BALOISE_INSURANCE,
    COMPREHENSIVE_INSURANCE,
    INSURANCE_ADDON,
    OMOCOM_BASIC,
    PLATFORM_INSURANCE,
    PRICE_TYPE_INCLUDED,
    PRICING_STRATEGY_PER_BOOKING,
} from '~/config/productTypes';
import BaloiseInsuranceOptions from '~/components/vehicle/BaloiseInsuranceOptions';
import OmocomInsuranceOptions from '~/components/vehicle/OmocomInsuranceOptions';
import OwnersInsuranceOptions from '~/components/vehicle/OwnersInsuranceOptions';
import { BALOISE_ID } from '~/config/insurance';

export default {
    name: 'VehicleInsuranceNew',
    components: {
        OwnersInsuranceOptions,
        OmocomInsuranceOptions,
        BaloiseInsuranceOptions,
    },
    props: {
        vehicle: {
            type: Object,
            default: () => {},
        },
        deductibleValues: {
            type: Object,
            default: () => {},
        },
        addons: {
            type: Array,
            default: null,
        },
        isOmocomInsurance: {
            type: Boolean,
            default: false,
        },
        isBaloiseInsurance: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            highlyRecommendedAddons: [INSURANCE_ADDON.roadside_assistance, INSURANCE_ADDON.deductible_buydown],
        };
    },
    computed: {
        filteredAdditionalAddons() {
            return this.orderedAdditionalAddons.filter(
                addon =>
                    (!!INSURANCE_ADDON[addon.key] || addon.type === PLATFORM_INSURANCE) && !this.isAddonMandatory(addon)
            );
        },
        allInsuranceAddons() {
            return this.includedAddons.concat(this.filteredAdditionalAddons).map(addon => {
                return {
                    ...addon,
                    title: addon.title || this.$t(`booking_addons.${addon.key}_title`),
                    priceLabel: this.getPriceLabel(addon),
                };
            });
        },
        orderedAdditionalAddons() {
            return this.$utils.mapOrder(this.addons, this.highlyRecommendedAddons, 'key');
        },
        includedAddons() {
            const addons = [];

            if (!this.isOmocomInsurance) {
                addons.push({
                    title: this.isBaloiseInsurance
                        ? this.$t('pages.booking_request.insurance_step.liability_and_comprehensive_insurance')
                        : this.$t('pages.booking_request.insurance_step.owners_insurance.title'),
                    priceType: PRICE_TYPE_INCLUDED,
                    key: this.isBaloiseInsurance ? BALOISE_INSURANCE : COMPREHENSIVE_INSURANCE,
                });
            }

            if (this.vehicle.insuranceIncludesRoadsideAssistance) {
                addons.push({
                    title: this.isBaloiseInsurance
                        ? this.$t('pages.booking_request.roadside_assistance')
                        : this.$t('pages.booking_request.insurance_step.owners_roadside_assistance.title'),
                    priceType: PRICE_TYPE_INCLUDED,
                    key: INSURANCE_ADDON.roadside_assistance,
                });
            }

            if (this.isInteriorInsuranceIncluded && !this.isOmocomInsurance) {
                addons.push({
                    title: this.$t('pages.booking_request.interior_insurance'),
                    priceType: PRICE_TYPE_INCLUDED,
                    key: INSURANCE_ADDON.interior_insurance,
                    vendor: BALOISE_ID, // needed to filter Baloise products in OwnersInsuranceOptions
                });
            }

            return addons;
        },
        isInteriorInsuranceIncluded() {
            return (
                this.vehicle.mandatoryAddons != null && this.vehicle.mandatoryAddons[INSURANCE_ADDON.interior_insurance]
            );
        },
        baseOmocomProduct() {
            return this.addons.find(addon => addon.key === OMOCOM_BASIC);
        },
    },
    methods: {
        isAddonMandatory(option) {
            return this.vehicle.mandatoryAddons?.[option.key] || false;
        },
        getPricePerNight(addon) {
            if (addon.pricingStrategy === PRICING_STRATEGY_PER_BOOKING && addon.price > 0) {
                const numOfNights = this.vehicle.pricing?.numberOfNights || 1;

                return addon.price / numOfNights;
            }

            if (this.isOmocomInsurance && addon.type === PLATFORM_INSURANCE) {
                return addon.price - this.baseOmocomProduct.price;
            }

            return addon.price;
        },
        getPriceLabel(addon) {
            const addonPrice = this.getPricePerNight(addon);

            if (addon?.priceType === PRICE_TYPE_INCLUDED || addonPrice === 0) {
                return this.$t('pages.vehicle.addon_included_in_price');
            }

            return `+ ${this.formattedPrice(addonPrice, addon.currency, 2)}`;
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';

.vehicle-insurance {
    ::v-deep &__insurance-by {
        display: flex;
        align-items: center;
        margin-bottom: 16px;

        span {
            margin-right: 8px;
            @include font-size(14px);
            font-weight: 600;
        }

        .insurance-logo {
            height: 18px;

            &--omocom {
                height: 26px;
                margin-top: -12px;
            }
        }
    }

    ::v-deep .vehicle-insurance-accordion {
        padding: 0;

        > .accordion__item {
            @extend %card;
            padding: 0;
            margin-bottom: 8px;

            > .accordion__header {
                padding: 16px !important;
                align-items: flex-start;

                > .accordion__title-wrapper {
                    display: block;

                    @include lg {
                        display: flex;
                        justify-content: space-between;
                        width: 100%;
                        padding-right: 12px;
                    }

                    > .accordion__title-text {
                        @include font-size(16px);
                        font-weight: bold;
                        margin-bottom: 8px;

                        @include lg {
                            margin-bottom: 0;
                        }
                    }
                }
            }

            > .accordion__content {
                padding: 0 16px 16px;
                @include font-size(14px);
                line-height: 20px;

                ul {
                    list-style: disc;
                    margin-left: 24px;

                    &:not(:last-child) {
                        margin-bottom: 24px;
                    }
                }

                .vehicle-insurance__list-title {
                    font-weight: 600;
                    margin-bottom: 0;
                }
            }
        }
    }
}
</style>
